import type { GetDesignPageDTO, GetDesignsDTO } from '@/typings/parsers/canvaApi';
import type {
    IsAuthenticatedResponse,
    GenericFileParserDesign,
    DesignPages,
    CanvaFileParserDesign,
    CanvaDesignExportRequest
} from '@/typings/parsers/externalFileParser';
import { CanvaExportFormat } from '@/typings/parsers/externalFileParser';
import api from '@/api/baseApi';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { useToaster } from '@/composables/toaster';

// TODO: onUnmounted is called when there is no active component instance to be associated with
const toaster = useToaster();

const canvaApiErrorCodes: Record<string, string> = {
    invalid_access_token:
        'The access token is invalid or has expired. Please re-authenticate to continue using the Canva integration.'
};

const executeWithErrorHandling = async <T>(req: Promise<AxiosResponse<T>>): Promise<AxiosResponse<T>> => {
    try {
        return await req;
    } catch (error) {
        const responseData = (error as AxiosError).response?.data;
        if (responseData) {
            let message = responseData.message || responseData.Message;
            if (responseData.code && canvaApiErrorCodes[responseData.code]) {
                message = canvaApiErrorCodes[responseData.code];
            }
            toaster.error(message);
        }
        throw error;
    }
};

export const isAuthenticated = async (username: string): Promise<IsAuthenticatedResponse> => {
    const response = await executeWithErrorHandling(
        api.get<IsAuthenticatedResponse>(`Users/ExternalFileParser/Authenticate?username=${username}`)
    );
    if (response.status == 404) {
        console.error('user not found');
        return { isAuthenticated: false, tokenRefreshed: false };
    }

    return response.data;
};

export const refreshAccessToken = async (username: string): Promise<void> => {
    await executeWithErrorHandling(api.get(`Users/ExternalFileParser/RefreshAccessToken?username=${username}`));
};

export const requestAccessToken = async (codeVerifier: string, code: string, username: string): Promise<void> => {
    await executeWithErrorHandling(
        api.post(`Users/ExternalFileParser/AccessToken`, {
            codeVerifier,
            code,
            username
        })
    );
};

export const getCanvaDesigns = async (
    token: string,
    next?: string,
    search?: string
): Promise<CanvaFileParserDesign> => {
    const url = `${process.env.VUE_APP_CANVA_API_URL}/designs?`;
    const response = await executeWithErrorHandling(
        axios.get<GetDesignsDTO>(url, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${token}`
            },
            params: next
                ? { continuation: next }
                : {
                      query: search || undefined,
                      sort_by: 'modified_descending'
                  }
        })
    );

    const { items, continuation } = response.data;
    const designs: GenericFileParserDesign[] = items.map((item) => ({
        id: item.id,
        title: item.title,
        thumbnailUrl: item.thumbnail?.url || '',
        pages: [{ pageNumber: 1, thumbnailUrl: '' }]
    }));

    return {
        next: continuation,
        designs
    };
};

export const getCanvaDesignPages = async (designId: string, token: string): Promise<DesignPages[]> => {
    const response = await executeWithErrorHandling(
        axios.get<GetDesignPageDTO>(`${process.env.VUE_APP_CANVA_API_URL}/designs/${designId}/pages`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${token}`
            }
        })
    );

    const { items } = response.data;
    const pages: DesignPages[] = items.map((item) => ({
        pageNumber: item.index,
        thumbnailUrl: item.thumbnail?.url || ''
    }));

    return pages;
};

export const exportCanvaDesign = async (
    body: CanvaDesignExportRequest
): Promise<{ succeedd: boolean; content: string }> => {
    try {
        const { format } = body;
        if (format !== CanvaExportFormat.PNG) {
            body.pngOptions = null;
        }
        const response = await executeWithErrorHandling(api.post<string>(`Users/ExternalFileParser/Export`, body));
        if (!response.data.length) {
            return {
                succeedd: false,
                content: `Couldn't import the canva file`
            };
        }
        return {
            succeedd: true,
            content: response.data
        };
    } catch (exception) {
        const axiosError = exception as AxiosError;
        const { Message: message } = axiosError.response?.data || {};
        return {
            succeedd: false,
            content: message
        };
    }
};
